import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { api } from "../../lib/api";
import { fcfa } from "../../lib/format";
import { ShopHeader } from "../../components/shop/ShopHeader";

export default function Merci() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const transactionId = params.get("transaction_id");
  const [state, setState] = useState({ status: "checking", order_number: "", total: 0 });
  const [attempts, setAttempts] = useState(0);

  const check = useCallback(async () => {
    if (!transactionId) { setState({ status: "unknown" }); return; }
    try {
      const { data } = await api.get(`/payments/paygate/status/${transactionId}`);
      setState({ status: data.status, order_number: data.order_number, total: data.total });
      return data.status;
    } catch {
      setState((s) => ({ ...s, status: "error" }));
      return "error";
    }
  }, [transactionId]);

  useEffect(() => {
    let timer;
    const run = async () => {
      const st = await check();
      if (st === "en_attente" && attempts < 12) {
        timer = setTimeout(() => setAttempts((a) => a + 1), 3500);
      }
    };
    run();
    return () => clearTimeout(timer);
  }, [check, attempts]);

  const paid = state.status === "paye";
  const failed = state.status === "echoue";
  const pending = state.status === "en_attente" || state.status === "checking";

  return (
    <div className="App min-h-screen bg-brand-cream">
      <ShopHeader />
      <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="merci-page">
        {pending && (
          <div data-testid="merci-pending">
            <Loader2 className="w-14 h-14 mx-auto text-brand animate-spin" />
            <h1 className="font-heading text-2xl font-black text-brand-ink mt-6">Vérification du paiement…</h1>
            <p className="text-brand-muted mt-2">Merci de patienter quelques secondes pendant que nous confirmons votre paiement.</p>
          </div>
        )}
        {paid && (
          <div data-testid="merci-success">
            <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-600" />
            <h1 className="font-heading text-3xl font-black text-brand-ink mt-6">Merci, commande confirmée !</h1>
            <p className="text-brand-muted mt-2">Votre paiement a bien été reçu. Nous préparons votre commande.</p>
            {state.order_number && <p className="mt-4 text-sm text-brand-sub">Numéro de commande : <span className="font-semibold text-brand-ink">{state.order_number}</span></p>}
            {state.total > 0 && <p className="text-sm text-brand-sub">Montant payé : <span className="font-semibold text-brand-ink">{fcfa(state.total)}</span></p>}
            <button onClick={() => navigate("/boutique")} data-testid="merci-continue" className="mt-8 bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-3 font-semibold">Continuer mes achats</button>
          </div>
        )}
        {failed && (
          <div data-testid="merci-failed">
            <XCircle className="w-16 h-16 mx-auto text-brand-secondary" />
            <h1 className="font-heading text-2xl font-black text-brand-ink mt-6">Paiement non abouti</h1>
            <p className="text-brand-muted mt-2">Votre paiement n'a pas été confirmé. Aucune somme n'a été prélevée. Vous pouvez réessayer.</p>
            <button onClick={() => navigate("/panier")} className="mt-8 bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-3 font-semibold">Retour au panier</button>
          </div>
        )}
        {(state.status === "unknown" || state.status === "error") && (
          <div data-testid="merci-unknown">
            <Clock className="w-14 h-14 mx-auto text-brand-muted" />
            <h1 className="font-heading text-2xl font-black text-brand-ink mt-6">Statut indisponible</h1>
            <p className="text-brand-muted mt-2">Nous n'avons pas pu vérifier ce paiement. Contactez-nous si vous avez été débité.</p>
            <button onClick={() => navigate("/boutique")} className="mt-8 bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-3 font-semibold">Retour à la boutique</button>
          </div>
        )}
      </div>
    </div>
  );
}
